/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AfterViewInit, Component, HostListener } from '@angular/core';
import { ErrorResponseModel } from '../../models';
import {
    KeyStoreService,
    LanguageService,
    LoadingService,
    LogService,
    ModalService,
    PGPService,
    UtilsService
} from '../../services';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-confirm-password',
    templateUrl: './confirmPassword.component.html',
    styleUrls: ['./confirmPassword.component.scss']
})

export class ConfirmPasswordComponent implements AfterViewInit {
    /**
     * Message to show in the body of the modal.
     */
    public message: string = 'no message set';

    /**
     * Give another component the option to close this modal.
     */
    public closeModalInternally: boolean = true;

    /**
     * Option to validate org key.
     */
    public getOrgKey: boolean = false;

    /**
     * Cancel callback to be called when user cancels the modal
     */
    public cancelCallBack: (...args: any[]) => void;

    /**
     * Callback to be call when validation has passed.
     */
    public callBack: (...args: any[]) => void;

    /**
     * New organisation form group.
     */
    public passwordForm: FormGroup;

    /**
     * Needed for subscribers as we are not extending the base component.
     * @protected
     */
    protected destroyed$: Subject<boolean> = new Subject();

    public constructor(
        private formBuilder: FormBuilder,
        private keyStoreService: KeyStoreService,
        private languageService: LanguageService,
        private loadingService: LoadingService,
        private logService: LogService,
        private modalService: ModalService,
        private pgpService: PGPService,
        private utilsService: UtilsService
    ) {
        this.setUpFormGroup();
        this.setupFormSubscriber();
    }

    public ngAfterViewInit() {
        this.modalService.openModalInParent('confirm-password');
    }

    /**
     * Add a host listener for checking the enter key.
     * @param event
     */
    @HostListener('document:keydown.enter', ['$event'])
    public onKeydownHandler(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.passwordForm.valid) {
            const element: HTMLElement = document.querySelector('#modal-confirm-user-password');
            if (element) {
                element.click();
            } else {
                this.logService.warn('ConfirmPasswordComponent: onKeydownHandler', 'No button found');
            }
        }
    }

    /**
     * Cancel callBack enables parent to destroy a modal component.
     */
    public cancelClicked(): void {
        this.cancelCallBack();
    }

    /**
     * Trigger Callback
     */
    public triggerCallBack(): void {
        if (this.closeModalInternally) {
            this.modalService.close('confirm-password');
        }

        const password = this.passwordForm.get('password').value;
        const apiCall = this.getApiCall(password);
        this.validatePassword(apiCall);
    }

    /**
     * Helper function to get correct API call.
     * @param userPassword
     * @private
     */
    private getApiCall(userPassword: string): any {
        const privateKey = this.keyStoreService.getPrivateKeyFromKeystore();
        if (this.getOrgKey) {
            const orgPrivateKey = this.keyStoreService.getPrivateKeyFromKeystore(this.getOrgKey);

            return this.pgpService.getDecryptedOrgKeyWithUserPassword(userPassword, privateKey, orgPrivateKey);
        } else {
            return this.pgpService.decryptPrivateKeyWithPassword(userPassword, privateKey, true);
        }
    }

    /**
     * Validate user password against private key(s).
     * @param apiCall
     * @private
     */
    private validatePassword(apiCall: any): void {
        const passwordField: AbstractControl = this.passwordForm.get('password');
        passwordField.disable();
        apiCall
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.callBack(passwordField.getRawValue());
                    if (this.closeModalInternally) {
                        this.passwordForm.reset();
                        this.loadingService.loadingButtonEmitter.next({
                            id: 'confirm-password',
                            state: false
                        });
                        this.modalService.close('confirm-password');
                    } else {
                        this.logService.debug(
                            'ConfirmPasswordComponent: validatePassword',
                            'Password validated successfully but the modal is to be closed by another component.'
                        );
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    passwordField.enable();
                    passwordField.setErrors({ 'decryptFail': `${this.languageService.translate('incorrect_password')}` });
                    this.logService.error('ConfirmPasswordComponent: validatePassword', error.message);
                    this.loadingService.loadingButtonEmitter.next({ id: 'confirm-password', state: false });
                }
            });
    }

    /**
     * Set up password form
     * @private
     */
    private setUpFormGroup(): void {
        this.passwordForm = this.formBuilder.group({
            password: [null, [Validators.required]]
        });
    }

    /**
     * Subscribe to form changes.
     * @private
     */
    private setupFormSubscriber(): void {
        const passwordInput: AbstractControl = this.passwordForm.get('password');
        passwordInput.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.utilsService.debounce('passwordInput', (): void => {
                        if (passwordInput.hasError('decryptFail')) {
                            passwordInput.setErrors({ 'decryptFail': null });
                        }
                    }, 250);
                }
            });
    }
}
